import React from "react";
import { CircleQuestionMark } from "lucide-react";


type HelpModalProps = {
    open: boolean;
    onClose: () => void;
};

const BrokerageFirmHelpModal = ({ open, onClose }: HelpModalProps) => {
    if (!open) return null;

    return (
        <div className="dc-overlay">
            <div className="dc-modal">
                {/* Close Button */}
                <button className="dc-close-btn" onClick={onClose}>
                    ✕
                </button>

                {/* Header */}
                <div className="dc-header">
                    <CircleQuestionMark className="dc-header-icon" style={{color:"#155dfc"}}/>
                    <h2 className="dc-header-title">Add Brokerage Firm – Quick Help</h2>
                </div>

                {/* Scrollable Content */}
                <div className="dc-content">

                    {/* Section Item */}
                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Parent Account:</h4>
                            <p className="dc-item-text">A Brokerage Firm is the parent account inside RealEsy for managing all its offices, agents, managers, and admins.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Brokerage-Specific Catalogs & Pricing:</h4>
                            <p className="dc-item-text">Define which products this brokerage can view and order in the RealEsy Marketplace, along with their custom pricing.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Create Hierarchy:</h4>
                            <p className="dc-item-text">After adding a brokerage, you can create offices, add new agents, or link existing RealEsy agents to this firm.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Vendor Management:</h4>
                            <p className="dc-item-text">Vendors can create and manage installation orders, removal orders, and sales orders tied to this brokerage's offices and inventory.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Custom Configuration:</h4>
                            <p className="dc-item-text">Each brokerage can have its own pricing rules, discounts, catalogs, billing details, and reporting structure within RealEsy.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Brand Consistency:</h4>
                            <p className="dc-item-text">The uploaded company image appears on orders, notifications, and brokerage-facing screens to maintain brand consistency.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Access Control:</h4>
                            <p className="dc-item-text">Email, phone, and status directly control the brokerage's access, activation, and visibility across RealEsy.</p>
                        </div>
                    </div>

                    <hr />

                    {/* Why This Matters */}
                    <div className="dc-section-title fs-16">
                        <span className="dc-section-icon">🔍</span>
                        Why This Matters
                    </div>

                    <p className="dc-item-text">
                        Creating a brokerage is the starting point for all RealEsy workflows—job scheduling, order processing, inventory assignment, office roles, and billing depend on this foundation.
                    </p>

                    <p className="dc-item-text">
                        When a new job is created, RealEsy looks at:
                    </p>

                    <ul className="dc-list">
                        <li><span style={{color:"#00a63e"}} className="fs-14 dc-item-text">✔</span> Job type (Installation/Removal)</li>
                        <li><span style={{color:"#00a63e"}} className="fs-14 dc-item-text ">✔</span> Origin Warehouse</li>
                        <li><span style={{color:"#00a63e"}} className="fs-14 dc-item-text">✔</span> The default cost setting</li>
                    </ul>

                    <hr />

                    {/* Notes */}
                    <div className="dc-section-title fs-16">
                        <span className="dc-section-icon">💡</span>
                        Helpful Notes
                    </div>

                    <ul className="dc-list" style={{ listStyleType: 'disc', paddingLeft: '1.25rem' }}>
                        <li className="dc-item-text">You can update brokerage details anytime without affecting existing jobs.</li>
                        <li className="dc-item-text">Inactive brokerages will not receive jobs or system notifications.</li>
                    </ul>
                </div>

                {/* Footer Button */}
                <div className="dc-footer">
                    <button className="dc-btn" onClick={onClose}>
                        Got it!
                    </button>
                </div>
            </div>
            <style>
                {`
         /* Overlay */
.dc-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.45);
  // backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Modal Box */
.dc-modal {
  background: #ffffff;
  width: 570px;
  max-height: 86vh;
  border-radius: 14px;
  padding: 0;
  position: relative;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Close Button */
.dc-close-btn {
  position: absolute;
  top: 14px;
  right: 20px;
  font-size: 20px;
  background: none;
  border: none;
  cursor: pointer;
}

/* Header */
.dc-header {
  display: flex;
  align-items: center;
  padding: 24px 32px 0;
  gap: 10px;
}

.dc-header-icon {
  font-size: 24px;
}

.dc-header-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0;
}

/* Scroll Content */
.dc-content {
  padding: 20px 30px;
  overflow-y: auto;
  flex: 1;
}

/* Blue Bar Left */
.dc-item {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.dc-blue-bar {
  width: 4px;
  background: #2563eb;
  border-radius: 4px;
}

/* Titles */
.dc-item-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.dc-item-text {
  margin: 4px 0;
  color: #555;
  font-size: 15px;
}

/* Section Title */
.dc-section-title {
  margin: 20px 0 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dc-section-icon {
  font-size: 20px;
}

/* List */
.dc-list {
  list-style: none;
  padding-left: 0;
}

.dc-list li {
  margin-bottom: 6px;
  color: #333;
}

/* Footer */
.dc-footer {
  padding: 16px 32px;
  border-top: 1px solid #eee;
}

.dc-btn {
  width: 100%;
  padding: 12px 0;
  background: #2563eb;
  border: none;
  color: white;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
}

.dc-btn:hover {
  background: #155dfc;
}
    @media (min-width: 768px) {
      .dc-modal {
        left:127px
      }
    }

        @media (max-width: 769px) {
      .dc-modal {
        left:0px
      }
    }

            `}
            </style>
        </div>
    );
};

export default BrokerageFirmHelpModal;

